import React, { useState, useEffect } from 'react';
import assetAPI, { AssetDependency, DependencyStats } from '../../services/assetAPI';
import {
  Package,
  RefreshCw,
  AlertTriangle,
  Shield,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Trash2,
  ExternalLink
} from 'lucide-react';

const DependencyViewer: React.FC = () => {
  const [dependencies, setDependencies] = useState<AssetDependency[]>([]);
  const [stats, setStats] = useState<DependencyStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    source: '',
    license_risk: '',
    has_vulnerabilities: false,
    is_copyleft: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [depsData, statsData] = await Promise.all([
        assetAPI.getDependencies({
          ...filters,
          search: searchTerm || undefined,
          has_vulnerabilities: filters.has_vulnerabilities || undefined,
          is_copyleft: filters.is_copyleft || undefined,
          source: filters.source || undefined,
          license_risk: filters.license_risk || undefined
        }),
        assetAPI.getDependencyStats()
      ]);
      setDependencies(depsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load dependencies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScan = async () => {
    setIsScanning(true);
    try {
      await assetAPI.scanDependencies();
      await loadData();
    } catch (error) {
      console.error('Failed to scan dependencies:', error);
    } finally {
      setIsScanning(false);
    }
  };

  const getLicenseRiskBadge = (risk: string) => {
    switch (risk) {
      case 'high':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            High Risk
          </span>
        );
      case 'medium':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            Medium
          </span>
        );
      case 'low':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            Low Risk
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const getEOLBadge = (status: string) => {
    switch (status) {
      case 'eol':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={10} /> EOL
          </span>
        );
      case 'deprecated':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            <AlertTriangle size={10} /> Deprecated
          </span>
        );
      case 'maintenance':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            Maintenance
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle size={10} /> Active
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Package Dependencies</h2>
        <button
          onClick={handleScan}
          disabled={isScanning}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
        >
          <RefreshCw size={16} className={isScanning ? 'animate-spin' : ''} />
          {isScanning ? 'Scanning...' : 'Scan Dependencies'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total</span>
              <Package size={18} className="text-blue-500" />
            </div>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{stats.total}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Backend</span>
              <Package size={18} className="text-purple-500" />
            </div>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{stats.bySource['backend'] || 0}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Frontend</span>
              <Package size={18} className="text-cyan-500" />
            </div>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{stats.bySource['frontend'] || 0}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Vulnerabilities</span>
              <AlertTriangle size={18} className="text-red-500" />
            </div>
            <p className={`text-2xl font-semibold mt-1 ${stats.vulnerabilities.packages_affected > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {stats.vulnerabilities.packages_affected}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Copyleft</span>
              <Shield size={18} className="text-amber-500" />
            </div>
            <p className={`text-2xl font-semibold mt-1 ${stats.licenses.copyleft > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {stats.licenses.copyleft}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">EOL Packages</span>
              <XCircle size={18} className="text-red-500" />
            </div>
            <p className={`text-2xl font-semibold mt-1 ${stats.eol_status.eol > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {stats.eol_status.eol}
            </p>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search packages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadData()}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        <select
          value={filters.source}
          onChange={(e) => {
            setFilters({ ...filters, source: e.target.value });
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Sources</option>
          <option value="backend">Backend</option>
          <option value="frontend">Frontend</option>
        </select>

        <select
          value={filters.license_risk}
          onChange={(e) => {
            setFilters({ ...filters, license_risk: e.target.value });
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All License Risks</option>
          <option value="high">High Risk</option>
          <option value="medium">Medium Risk</option>
          <option value="low">Low Risk</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.has_vulnerabilities}
            onChange={(e) => setFilters({ ...filters, has_vulnerabilities: e.target.checked })}
            className="h-4 w-4 text-blue-600 rounded border-gray-300"
          />
          Has Vulnerabilities
        </label>

        <button
          onClick={loadData}
          className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Apply
        </button>
      </div>

      {/* Dependencies Table */}
      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-500">Loading dependencies...</span>
          </div>
        </div>
      ) : dependencies.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900">No dependencies found</h3>
          <p className="text-sm text-gray-500 mt-1">
            Click "Scan Dependencies" to discover packages from package.json files.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  License
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vulnerabilities
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dependencies.map((dep) => (
                <tr key={dep.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Package size={16} className={dep.is_dev_dependency ? 'text-gray-400' : 'text-blue-500'} />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{dep.package_name}</div>
                        {dep.is_dev_dependency && (
                          <span className="text-xs text-gray-400">dev dependency</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{dep.package_version || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      dep.source === 'backend' ? 'bg-purple-100 text-purple-800' : 'bg-cyan-100 text-cyan-800'
                    }`}>
                      {dep.source}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getLicenseRiskBadge(dep.license_risk)}
                      {dep.is_copyleft && (
                        <span className="text-xs text-amber-600" title="Copyleft License">
                          (Copyleft)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {dep.vulnerability_count > 0 ? (
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={14} className="text-red-500" />
                        <span className="text-sm text-red-600">{dep.vulnerability_count}</span>
                        {dep.critical_vulnerabilities > 0 && (
                          <span className="text-xs text-red-600">({dep.critical_vulnerabilities} critical)</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-green-600 flex items-center gap-1">
                        <CheckCircle size={14} /> None
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getEOLBadge(dep.eol_status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DependencyViewer;
